import { useEffect, useState } from "react";

// Generate a unique ID for this script load (resets on page refresh)
const SCRIPT_LOAD_ID = String(Math.random());

// Module-level cache for tab ID and page session ID (shared across all hook instances in same script load)
let cachedTabId: string | null = null;
let cachedPageSessionId: string | null = null;
let hasInitializedTabId = false;
let hasInitializedPageSessionId = false;
let activeHookInstances = 0; // Track active hook instances

// Initialize tab ID (for tab isolation)
const initializeTabId = () => {
  if (hasInitializedTabId && cachedTabId) {
    return cachedTabId;
  }
  
  hasInitializedTabId = true;
  
  const existingTabId = sessionStorage.getItem('tabID');
  const tabCreatedAt = sessionStorage.getItem('__tabCreatedAt');
  const now = Date.now();
  
  if (!existingTabId) {
    // Brand new tab
    cachedTabId = String(Math.random());
    sessionStorage.setItem('tabID', cachedTabId);
    sessionStorage.setItem('__tabCreatedAt', String(now));
  } else if (tabCreatedAt && (now - parseInt(tabCreatedAt)) < 50) {
    // Duplicated tab (sessionStorage copied, but very recent)
    cachedTabId = String(Math.random());
    sessionStorage.setItem('tabID', cachedTabId);
    sessionStorage.setItem('__tabCreatedAt', String(now));
  } else {
    // Page refresh or first load in existing tab
    cachedTabId = existingTabId;
    sessionStorage.setItem('__tabCreatedAt', String(now));
  }
  
  return cachedTabId;
};

// Initialize page session ID (for SPA navigation detection)
const initializePageSessionId = () => {
  // If already initialized during this script load, return cached value
  if (hasInitializedPageSessionId && cachedPageSessionId) {
    return cachedPageSessionId;
  }
  
  hasInitializedPageSessionId = true;
  
  const storedScriptLoadId = sessionStorage.getItem('__scriptLoadId');
  const storedPageSessionId = sessionStorage.getItem('__pageSessionId');
  
  // Check if this is a new script load (page refresh) or same script load (SPA navigation)
  if (storedScriptLoadId !== SCRIPT_LOAD_ID) {
    // New script load (page refresh or first load)
    sessionStorage.setItem('__scriptLoadId', SCRIPT_LOAD_ID);
    
    if (storedPageSessionId) {
      // Page refresh - reuse existing page session ID
      cachedPageSessionId = storedPageSessionId;
    } else {
      // First load - create new page session ID
      cachedPageSessionId = String(Math.random());
      sessionStorage.setItem('__pageSessionId', cachedPageSessionId);
    }
  } else {
    // Same script load means component remounted (SPA navigation back)
    // Generate NEW page session ID to clear state
    cachedPageSessionId = String(Math.random());
    sessionStorage.setItem('__pageSessionId', cachedPageSessionId);
  }
  
  return cachedPageSessionId;
};

export function useTabPersistedState(key: string, defaultValue: any) {
    // Get tab ID (for tab isolation, shared across all hooks in same component)
    const [tabId] = useState(() => initializeTabId());
    
    // Get page session ID (for SPA navigation detection, unique per component mount)
    const [pageSessionId] = useState(() => initializePageSessionId());
    
    const storageKey = `${key}_${tabId}_${pageSessionId}`;
  
    const [state, setState] = useState(() => {
      const saved = localStorage.getItem(storageKey);    
      return saved !== null ? JSON.parse(saved) : defaultValue;
    });
  
    useEffect(() => {
      localStorage.setItem(storageKey, JSON.stringify(state));
    }, [storageKey, state]);
    
    // Track hook instances and reset page session cache when all unmount
    useEffect(() => {
      // Increment on mount
      activeHookInstances++;
      
      return () => {
        // Decrement on unmount
        activeHookInstances--;
        
        // If no active instances, reset page session cache
        // This ensures fresh state when component remounts after navigation
        if (activeHookInstances === 0) {
          cachedPageSessionId = null;
          hasInitializedPageSessionId = false;
        }
      };
    }, []);
  
    return [state, setState];
  }
  
  
  